import { Controller } from '@hotwired/stimulus'

import achCheckingIcon from '../images/ach_checking.svg'
import achSavingsIcon from '../images/ach_savings.svg'
import americanExpressIcon from '../images/american-express.svg'
import discoverIcon from '../images/discover.svg'
import mastercardIcon from '../images/mastercard.svg'
import visaIcon from '../images/visa.svg'

const paymentMethodIcons = {
  ach_checking: achCheckingIcon,
  ach_savings: achSavingsIcon,
  american_express: americanExpressIcon,
  discover: discoverIcon,
  mastercard: mastercardIcon,
  visa: visaIcon
}

export default class extends Controller {
  static targets = [
    'addButton',
    'error',
    'list',
    'loading'
  ]

  static values = {
    customerId: Number,
    indexUrl: String,
    tacUrl: String
  }

  connect () {
    this.paymentMethods = []

    this.highlightPaymentMethodId =
    new URLSearchParams(window.location.search).get('highlight')

    this.load()
  }

  async load () {
    this.clearError()

    try {
      const response = await fetch(this.indexUrlValue, {
        headers: {
          Accept: 'application/json'
        },
        credentials: 'same-origin'
      })

      const body = await this.parseResponse(response)

      if (!response.ok) {
        throw new Error(
          body.error || 'Payment methods could not be loaded.'
        )
      }

      this.paymentMethods = [...(body.payment_methods || [])].sort(
        (left, right) => {
          const leftDate = left.used_at
            ? new Date(left.used_at)
            : new Date(0)

          const rightDate = right.used_at
            ? new Date(right.used_at)
            : new Date(0)

          return rightDate - leftDate
        }
      )

      this.render()
    } catch (error) {
      console.error(error)
      this.showError(error.message)
      this.render()
    }
  }

  render () {
    this.listTarget.replaceChildren()

    if (this.paymentMethods.length === 0) {
      this.listTarget.append(this.emptyState())
      return
    }

    for (const paymentMethod of this.paymentMethods) {
      this.listTarget.append(
        this.paymentMethodCard(paymentMethod)
      )
    }

    this.scrollToHighlightedPaymentMethod()
  }

  scrollToHighlightedPaymentMethod () {
    if (!this.highlightPaymentMethodId) {
      return
    }

    const element = this.listTarget.querySelector(
    `[data-payment-method-id="${this.highlightPaymentMethodId}"]`
    )

    element?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    })
  }

  emptyState () {
    const container = document.createElement('div')

    container.className = [
      'rounded-lg',
      'border',
      'border-dashed',
      'border-gray-300',
      'bg-white',
      'p-8',
      'text-center',
      'sm:col-span-2',
      'lg:col-span-3'
    ].join(' ')

    const heading = document.createElement('p')
    heading.className = 'text-sm font-semibold text-gray-900'
    heading.textContent = 'No saved payment methods'

    const description = document.createElement('p')
    description.className = 'mt-1 text-sm text-gray-600'
    description.textContent =
      'Add a payment method to use it for future invoice payments.'

    container.append(heading, description)

    return container
  }

  paymentMethodCard (paymentMethod) {
    const container = document.createElement('article')

    const isHighlighted =
      this.highlightPaymentMethodId === paymentMethod.id.toString()

    container.className = [
      'rounded-lg',
      'border',
      isHighlighted
        ? 'border-amber-400 ring-2 ring-amber-400'
        : this.highlightPaymentMethodId
          ? 'border-gray-200 bg-white'
          : paymentMethod.default
            ? 'border-blue-500 ring-1 ring-blue-500 bg-white'
            : 'border-gray-200 bg-white',
      'p-5',
      'shadow-sm'
    ].join(' ')

    const header = document.createElement('div')
    header.className = 'flex items-start justify-between gap-4'

    const identity = document.createElement('div')
    identity.className = 'flex items-center gap-3'

    const iconPath = paymentMethodIcons[paymentMethod.account_type]

    if (iconPath) {
      const icon = document.createElement('img')

      icon.src = iconPath
      icon.alt = ''
      icon.className = 'h-8 w-12 shrink-0 object-contain'

      identity.append(icon)
    }

    const identityText = document.createElement('div')

    const type = document.createElement('p')
    type.className = 'text-sm font-semibold text-gray-900'
    type.textContent = this.accountTypeLabel(
      paymentMethod.account_type
    )

    const accountNumber = document.createElement('p')
    accountNumber.className =
  'mt-1 font-mono text-sm text-gray-600'
    accountNumber.textContent = this.maskedAccountNumber(
      paymentMethod.masked_account_number
    )

    identityText.append(type, accountNumber)
    identity.append(identityText)
    if (paymentMethod.default) {
      const badge = document.createElement('span')

      badge.className = [
        'inline-flex',
        'rounded-full',
        'bg-blue-100',
        'px-2.5',
        'py-0.5',
        'text-xs',
        'font-semibold',
        'text-blue-800'
      ].join(' ')

      badge.textContent = 'Default'
      header.append(identity, badge)
    } else {
      header.append(identity)
    }

    const details = document.createElement('dl')
    details.className = 'mt-5 space-y-2 text-sm'

    if (paymentMethod.month && paymentMethod.year) {
      details.append(
        this.detailRow(
          'Expires',
          this.expirationLabel(
            paymentMethod.month,
            paymentMethod.year
          )
        )
      )
    }

    details.append(
      this.detailRow(
        'Last used/updated',
        this.dateLabel(paymentMethod.used_at)
      )
    )

    details.append(
      this.detailRow(
        'Added',
        this.dateLabel(paymentMethod.created_at)
      )
    )

    const actions = document.createElement('div')
    actions.className =
      'mt-5 flex flex-wrap items-center justify-end gap-3 border-t border-gray-100 pt-4'

    const updateButton = document.createElement('button')
    updateButton.type = 'button'
    updateButton.className = [
      'text-sm',
      'font-semibold',
      'text-blue-600',
      'hover:text-blue-800',
      'disabled:cursor-not-allowed',
      'disabled:text-gray-400'
    ].join(' ')

    updateButton.textContent = 'Update'

    updateButton.addEventListener('click', async () => {
      this.clearError()
      updateButton.disabled = true

      try {
        await this.openPayPage(paymentMethod.id)
      } catch (error) {
        console.error(error)
        this.showError(error.message)
        updateButton.disabled = false
      }
    })

    const defaultButton = document.createElement('button')
    defaultButton.type = 'button'
    defaultButton.className = [
      'text-sm',
      'font-semibold',
      'text-blue-600',
      'hover:text-blue-800',
      'disabled:cursor-not-allowed',
      'disabled:text-gray-400'
    ].join(' ')

    defaultButton.textContent = paymentMethod.default
      ? 'Remove Default'
      : 'Make Default'

    defaultButton.addEventListener('click', () => {
      this.setDefault(
        paymentMethod,
        !paymentMethod.default,
        defaultButton
      )
    })

    const removeButton = document.createElement('button')
    removeButton.type = 'button'
    removeButton.className = [
      'text-sm',
      'font-semibold',
      'text-red-600',
      'hover:text-red-800',
      'disabled:cursor-not-allowed',
      'disabled:text-gray-400'
    ].join(' ')

    removeButton.textContent = 'Remove'

    removeButton.addEventListener('click', () => {
      this.remove(paymentMethod, removeButton)
    })

    actions.append(updateButton, defaultButton, removeButton)
    container.append(header, details, actions)

    return container
  }

  detailRow (label, value) {
    const row = document.createElement('div')
    row.className = 'flex items-center justify-between gap-4'

    const term = document.createElement('dt')
    term.className = 'text-gray-500'
    term.textContent = label

    const description = document.createElement('dd')
    description.className = 'text-right text-gray-900'
    description.textContent = value

    row.append(term, description)

    return row
  }

  async add () {
    this.clearError()
    this.addButtonTarget.disabled = true
    this.addButtonTarget.textContent = 'Opening secure page…'

    try {
      await this.openPayPage()
    } catch (error) {
      console.error(error)
      this.showError(error.message)

      this.addButtonTarget.disabled = false
      this.addButtonTarget.textContent = 'Add Payment Method'
    }
  }

  async openPayPage (paymentMethodId = null) {
    const response = await fetch(this.tacUrlValue, {
      method: 'POST',
      headers: this.jsonHeaders,
      credentials: 'same-origin',
      body: JSON.stringify({
        tac: {
          amount: '0.00',
          order_amounts: {},
          note: '',
          tran_code: 'STORAGE',
          application_type: 'AddPaymentMethod',
          customer_id: this.customerIdValue,
          payment_method_id: paymentMethodId
        }
      })
    })

    const body = await this.parseResponse(response)

    if (!response.ok || body.error) {
      throw new Error(
        body.error ||
      'The secure payment page could not be opened.'
      )
    }

    this.postForm(body.paypage_url, body.parameters)
  }

  async setDefault (paymentMethod, state, button) {
    this.clearError()
    button.disabled = true

    try {
      const response = await fetch(
        paymentMethod.routes.default,
        {
          method: 'PATCH',
          headers: this.jsonHeaders,
          credentials: 'same-origin',
          body: JSON.stringify({
            default: state
          })
        }
      )

      const body = await this.parseResponse(response)

      if (!response.ok) {
        throw new Error(
          body.error ||
          'The default payment method could not be updated.'
        )
      }

      this.clearHighlight()
      await this.load()
    } catch (error) {
      console.error(error)
      this.showError(error.message)
      button.disabled = false
    }
  }

  async remove (paymentMethod, button) {
    const confirmed = window.confirm(
      'Are you sure you want to remove this payment method?'
    )

    if (!confirmed) {
      return
    }

    this.clearError()
    button.disabled = true

    try {
      const response = await fetch(
        paymentMethod.routes.remove,
        {
          method: 'DELETE',
          headers: this.jsonHeaders,
          credentials: 'same-origin'
        }
      )

      const body = await this.parseResponse(response)

      if (!response.ok) {
        throw new Error(
          body.error ||
          'The payment method could not be removed.'
        )
      }

      await this.load()
    } catch (error) {
      console.error(error)
      this.showError(error.message)
      button.disabled = false
    }
  }

  postForm (path, parameters) {
    const form = document.createElement('form')

    form.method = 'post'
    form.action = path

    for (const [name, value] of Object.entries(parameters)) {
      const input = document.createElement('input')

      input.type = 'hidden'
      input.name = name
      input.value = value

      form.append(input)
    }

    document.body.append(form)
    form.submit()
  }

  clearHighlight () {
    this.highlightPaymentMethodId = null

    const url = new URL(window.location.href)
    url.searchParams.delete('highlight')
    window.history.replaceState({}, '', url)
  }

  accountTypeLabel (accountType) {
    const labels = {
      ach_checking: 'Checking Account',
      ach_savings: 'Savings Account',
      american_express: 'American Express',
      discover: 'Discover',
      mastercard: 'Mastercard',
      visa: 'Visa'
    }

    return labels[accountType] ||
      accountType
        ?.replaceAll('_', ' ')
        .replace(/\b\w/g, character => character.toUpperCase()) ||
      'Payment Method'
  }

  maskedAccountNumber (accountNumber) {
    if (!accountNumber) {
      return 'Account number unavailable'
    }

    return accountNumber.slice(-8)
  }

  dateLabel (date) {
    if (!date) {
      return 'Never'
    }

    return new Date(date).toLocaleDateString()
  }

  expirationLabel (month, year) {
    if (!month || !year) {
      return 'Not available'
    }

    return `${month}/${year}`
  }

  async parseResponse (response) {
    try {
      return await response.json()
    } catch (_error) {
      return {}
    }
  }

  showError (message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove('hidden')
  }

  clearError () {
    this.errorTarget.textContent = ''
    this.errorTarget.classList.add('hidden')
  }

  get jsonHeaders () {
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute('content') || ''
    }
  }
}
